/* Progressive reveal for collection grids: 25 cards on load, +25 per "Show More" click.
   Composes with ef-collection-filter.js — the filter hides non-matching cards via the
   [hidden] property; this layer only caps the cards the filter left visible using the
   .ef-card-capped class, and resets to 25 whenever the filter or sort changes
   (ef:collf:applied). The card pool comes from ef-collection-rest.js: 25 rendered in the
   document plus the tail it fetched from ?view=cards. STEP must match the limit in the
   collection templates and the offset in templates/collection.cards.liquid.

   Keyboard and Back (post-launch review, 2026-10-14):
   - Show More moves focus to the first card it revealed and says the new count in a polite
     live region, so the next Tab lands in the new cards, not on the links below the grid.
   - The expanded count is kept in this history entry's state (key efCollMore, merged like
     ef-housing-selector.js does), tied to the URL incl. filter params and to the sort order.
     A Back from a product page that was opened from card 26+ rebuilds that many cards once
     the ?view=cards fetch has settled, then keeps the visited card on screen (and focused,
     if it was opened from the keyboard). The server-rendered first 25 never change. */
(function () {
  'use strict';
  var STEP = 25;
  var STATE_KEY = 'efCollMore';
  var LINK = '.ef-coll-card__title-link';

  /* A scroll or key press from the visitor before the restore runs wins over the restore. */
  var visitorMoved = false;
  ['wheel', 'touchmove', 'keydown', 'mousedown'].forEach(function (type) {
    window.addEventListener(type, function () { visitorMoved = true; }, { capture: true, passive: true, once: true });
  });

  /* The view a saved count belongs to: path + query (filters, ?pack=, ?q=), key order ignored. */
  function viewKey() {
    var p = new URLSearchParams(window.location.search);
    if (p.sort) p.sort();
    var q = p.toString();
    return window.location.pathname + (q ? '?' + q : '');
  }

  function readSaved() {
    try {
      var state = window.history.state;
      var saved = state && typeof state === 'object' ? state[STATE_KEY] : null;
      return saved && typeof saved === 'object' ? saved : null;
    } catch (_) { return null; }
  }

  /* Merge into history.state so other components' keys (efHousingSelection, …) survive;
     never overwrite a non-object state some other script owns. null removes our key. */
  function writeSaved(entry) {
    try {
      var state = window.history.state;
      if (state !== null && state !== undefined && (typeof state !== 'object' || Array.isArray(state))) return;
      if (!entry && !(state && Object.prototype.hasOwnProperty.call(state, STATE_KEY))) return;
      var next = Object.assign({}, state);
      if (entry) next[STATE_KEY] = entry;
      else delete next[STATE_KEY];
      window.history.replaceState(Object.keys(next).length ? next : null, '');
    } catch (_) { /* Show More still works when history writes are unavailable or throttled. */ }
  }

  function cardLink(card) {
    return card.querySelector(LINK) || card.querySelector('a[href]:not([tabindex="-1"])');
  }

  function focusCard(card, preventScroll) {
    var target = cardLink(card);
    if (!target) {
      if (!card.hasAttribute('tabindex')) card.setAttribute('tabindex', '-1');
      target = card;
    }
    try { target.focus({ preventScroll: !!preventScroll }); } catch (_) { target.focus(); }
  }

  function arrivedByHistory() {
    try {
      var nav = window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      return !nav || nav.type === 'back_forward';
    } catch (_) { return true; }
  }

  function init() {
    var grid = document.querySelector('.ef-coll-grid');
    var wrap = document.querySelector('[data-ef-more]');
    if (!grid || !wrap) return;
    var btn = wrap.querySelector('[data-ef-more-btn]');
    var limit = STEP;
    var rendered = [];
    var visibleCount = 0;
    /* The order the grid is in. The filter always builds it in server (featured) order and
       only re-sorts on a change of [data-ef-sort]; the select's own value can be stale after
       the browser restores form fields, so it is tracked here instead of read back. */
    var sort = 'featured';
    if (!window.EFCollectionCards) {
      window.EFCollectionCards = window.EFCollectionRest
        ? window.EFCollectionRest.cards.slice()
        : Array.prototype.slice.call(grid.querySelectorAll('.ef-coll-card'));
    }

    /* Outside [data-ef-more], which is hidden once every card is shown. */
    var status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('data-ef-more-status', '');
    wrap.parentNode.insertBefore(status, wrap.nextSibling);

    function applyCap() {
      var cards = window.EFCollectionCards || Array.prototype.slice.call(grid.querySelectorAll('.ef-coll-card'));
      var visible = cards.filter(function (card) { return !card.hidden; });
      rendered = visible.slice(0, limit);
      visibleCount = visible.length;
      cards.forEach(function (card) {
        card.classList.remove('ef-card-capped');
        if (card.parentNode === grid) card.remove();
      });
      var fragment = document.createDocumentFragment();
      rendered.forEach(function (card) { fragment.appendChild(card); });
      grid.appendChild(fragment);
      var capped = Math.max(0, visible.length - rendered.length);
      wrap.hidden = capped === 0;
      if (btn && capped > 0) {
        btn.textContent = 'Show More (' + capped + ' remaining)';
      }
    }

    function save(visited) {
      if (limit <= STEP && !visited) { writeSaved(null); return; }
      var entry = { url: viewKey(), sort: sort, shown: limit };
      if (visited) { entry.card = visited.url; entry.kbd = visited.kbd; }
      writeSaved(entry);
    }

    if (btn) {
      btn.addEventListener('click', function () {
        var before = rendered.length;
        limit += STEP;
        applyCap();
        var shown = rendered.length;
        status.textContent = shown < visibleCount
          ? 'Showing ' + shown + ' of ' + visibleCount + ' filters'
          : 'Showing all ' + visibleCount + ' filters';
        save(null);
        /* Focus moves before the hidden button can drop it to <body>; focus() scrolls the
           card into view if it is not already. */
        var first = rendered[before] || rendered[rendered.length - 1];
        if (first) focusCard(first, false);
      });
    }

    /* Remember which card was opened, so Back can keep it on screen. Plain left clicks and
       keyboard activation only (a click with detail 0 came from the keyboard); a new-tab
       click leaves this page where it is. */
    grid.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest && e.target.closest('a[href]');
      var card = link && link.closest('.ef-coll-card');
      if (!card || card.parentNode !== grid) return;
      var url = card.getAttribute('data-product-url') || link.getAttribute('href');
      save({ url: url, kbd: e.detail === 0 });
    });

    document.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-ef-sort]')) sort = e.target.value || 'featured';
    });
    document.addEventListener('ef:collf:applied', function () {
      limit = STEP;
      applyCap();
      status.textContent = '';
      writeSaved(null);
    });

    function savedFor() {
      var saved = readSaved();
      if (!saved || saved.url !== viewKey() || saved.sort !== sort) return null;
      var shown = parseInt(saved.shown, 10);
      return shown >= STEP && shown < 100000 ? { shown: shown, card: saved.card, kbd: !!saved.kbd } : null;
    }

    /* Back from the product page: the grid was already rebuilt to the saved count (this runs
       only after EFCollectionRest settled, so cards 26+ exist). Make sure the visited card is
       on screen; if the browser's own scroll restoration already put it there, nothing moves. */
    function returnToCard(saved) {
      save(null); /* one-shot: a later reload or Back restores the count, not this card */
      if (!arrivedByHistory()) return;
      var card = null;
      for (var i = 0; i < rendered.length; i++) {
        if (rendered[i].getAttribute('data-product-url') === saved.card) { card = rendered[i]; break; }
      }
      if (!card) return;
      var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
      raf(function () {
        if (visitorMoved) return;
        if (saved.kbd && !grid.contains(document.activeElement)) focusCard(card, true);
        var box = card.getBoundingClientRect();
        var viewH = window.innerHeight || document.documentElement.clientHeight;
        if (box.bottom <= 0 || box.top >= viewH) {
          try { card.scrollIntoView({ block: 'center' }); } catch (_) { card.scrollIntoView(); }
        }
      });
    }

    var initial = savedFor();
    if (initial) limit = Math.max(STEP, initial.shown);
    applyCap();
    if (initial && initial.card) returnToCard(initial);

    /* bfcache keeps the DOM, so this is normally a no-op; it covers a restored page whose
       grid was rebuilt smaller than the state it left with. */
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var saved = savedFor();
      if (saved && saved.shown > limit) { limit = saved.shown; applyCap(); }
    });
  }

  function boot() {
    if (window.EFCollectionRest) window.EFCollectionRest.ready(init);
    else init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
